import { Outlet, useNavigate } from 'react-router-dom';
import Loading from '../ions/Loading';
import PageHeader from '../molecules/PageHeader';
import useFetch, { FetchHeader } from '../../hooks/useFetch';
import { ReactNode, useRef, useState, useEffect } from 'react';
import PageFooter from '../molecules/PageFooter';

const GRID_MARGIN_TOP_DEFAULT = 70;

interface BreadcrumbItem {
    label: string;
    url: string;
}

type PageGridProps = {
    pageTitle: string;
    url: string;
    fetchHeaders?: FetchHeader[];
    breadcrumb: BreadcrumbItem[];
    children: {
        controlSlot: ReactNode;
        filterSlot: ReactNode;
        UIComponents?: ReactNode;
    };
    renderCards: (rows: never[]) => ReactNode;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (pageSize: number) => void;
    itemsLabel: string;
    defaultBackendHeader?: boolean;
    isLoadingData?: boolean;
    filterRows?: (row: any) => boolean;
    onGotApiPayload?: (data: any) => void;
    gridMarginTop?: number;
};

const PageGrid = ({
    pageTitle,
    url,
    fetchHeaders,
    breadcrumb,
    children,
    renderCards,
    page,
    pageSize,
    onPageChange,
    onItemsPerPageChange,
    defaultBackendHeader = true,
    itemsLabel,
    isLoadingData = false,
    filterRows = () => true,
    onGotApiPayload = () => { },
    gridMarginTop = GRID_MARGIN_TOP_DEFAULT,
}: PageGridProps) => {
    const navigate = useNavigate();

    const rows = useRef([]);
    const size = useRef(0);
    const apiVersion = useRef('');
    const totalPages = useRef(0);
    const totalElements = useRef(0);
    const numberOfElements = useRef(0);

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const calculateWindowHeight = () => {
        setWindowHeight(window.innerHeight - gridMarginTop);
    };

    useEffect(() => {
        calculateWindowHeight();

        window.addEventListener('resize', calculateWindowHeight);

        return () => {
            window.removeEventListener('resize', calculateWindowHeight);
        };
    }, [gridMarginTop]);

    const { controlSlot, filterSlot, UIComponents } = children;

    const { isLoading, data, error, isTokenError } = useFetch(url, fetchHeaders);

    useEffect(() => {
        if (isTokenError) {
            navigate('/denied');
        } else if (error) {
            navigate('/network-error');
        }
    }, [isTokenError, error, navigate]);

    if (data) {
        apiVersion.current = `${data.apiName} - ${data.apiVersion}`;

        let allRows = data.list || [];
        if (allRows instanceof Array) {
            allRows = allRows.filter((row: any) => filterRows(row));
        } else {
            allRows = [];
        }

        // Paginação frontend
        totalElements.current = allRows.length;
        totalPages.current = Math.ceil(allRows.length / pageSize);

        const startIndex = page * pageSize;
        const endIndex = startIndex + pageSize;
        rows.current = allRows.slice(startIndex, endIndex);

        numberOfElements.current = rows.current.length;
        size.current = pageSize;

        if (onGotApiPayload) onGotApiPayload(data);
    }

    return (
        <div className="px-4">
            <div className="grid grid-cols-1">
                {(isLoadingData || isLoading) && <Loading />}
                <PageHeader title={pageTitle} apiVersion={apiVersion.current} breadcrumb={breadcrumb}>
                    <div className="pt-4">{controlSlot}</div>
                </PageHeader>

                {filterSlot}

                <div
                    className="flex flex-col"
                    style={{ height: windowHeight }}
                >
                    <div className="flex-1 overflow-y-auto">
                        {Array.isArray(rows.current) ? renderCards(rows.current) : null}
                    </div>

                    <div className="flex-shrink-0 border-t bg-white mt-4">
                        <PageFooter
                            currentPage={page}
                            totalPages={totalPages.current}
                            itemsPerPage={pageSize}
                            totalElements={totalElements.current}
                            onPageChange={onPageChange}
                            onItemsPerPageChange={onItemsPerPageChange}
                            itemsLabel={itemsLabel}
                        />
                    </div>
                </div>

                <Outlet />
                {UIComponents}
            </div>
        </div>
    );
};

export default PageGrid;
